import { useEffect } from 'react';
import { useToaster } from 'pixelize-design-library';

import i18n, { ensureTamilBundleLoaded } from '@core/i18n';
import { settings } from '@core/store/Settings/settingsSlice';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';

import { setLanguageLoadPending } from './languageLoadStatus';

// Redux → i18next, one direction. `ta` loads on demand; a failed load reverts to `en`.
export function useLanguageSync(): void {
  const language = useAppSelector((s) => s.settings.language);
  const dispatch = useAppDispatch();
  const { showToast } = useToaster();

  useEffect(() => {
    if (i18n.language === language) return;

    if (language !== 'ta') {
      void i18n.changeLanguage(language);
      return;
    }

    let cancelled = false;
    setLanguageLoadPending(true);

    ensureTamilBundleLoaded()
      .then(() => {
        if (!cancelled) return i18n.changeLanguage('ta');
      })
      .catch(() => {
        if (cancelled) return;
        dispatch(settings({ language: 'en' }));
        showToast({ title: i18n.t('appError.generic'), status: 'error' });
      })
      .finally(() => {
        if (!cancelled) setLanguageLoadPending(false);
      });

    return () => {
      cancelled = true;
    };
    // `t` from useTranslation() is deliberately not used here — its identity changes the
    // instant i18n.language flips, which would re-fire this very effect mid-flight (cleanup
    // racing the in-flight .finally()) and leave isPending stuck true. i18n.t() below reads
    // the same translations without that self-referential dependency.
  }, [language, dispatch, showToast]);
}
